const DEFAULT_API_KEY = 'service-api-key-01KFH1VD5F9DC2NTR2HX8QB8V3';

/** Use Vite proxy in dev to avoid CORS, direct URL in production. */
function getExternalMogenBase(): string {
  if (import.meta.env.DEV) {
    return '/external-mogen-proxy';
  }
  const env = import.meta.env.VITE_API_DISAMBIGUATOR ?? '';
  return `https://external-mogen${env}.api.getcartwheel.com`;
}

interface MotionResponse {
  motionID: string;
  status: string;
  bvhURL?: string;
  sourceBvhURL?: string;
  errorMessage?: string;
}

interface MotionEditResponse {
  editID?: string;
  bvhURL: string;
}

interface MotionSaveResponse {
  motionID: string;
}

/**
 * GET /motion/{motionID} — fetch motion metadata including presigned BVH URL.
 */
export async function fetchMotion(
  motionID: string,
  apiKey?: string,
): Promise<MotionResponse> {
  const base = getExternalMogenBase();
  const url = `${base}/motion/${encodeURIComponent(motionID)}`;
  const res = await fetch(url, {
    headers: { 'x-api-key': apiKey || DEFAULT_API_KEY },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to fetch motion (${res.status}): ${body || res.statusText}`,
    );
  }
  return res.json();
}

/**
 * Download raw BVH text from a presigned S3 URL.
 */
export async function fetchBVHText(bvhURL: string): Promise<string> {
  const res = await fetch(bvhURL);
  if (!res.ok) throw new Error(`Failed to download BVH: ${res.status}`);
  return res.text();
}

/**
 * POST /motion/edit — submit constraints and get a new motionID.
 */
export async function editMotion(
  motionID: string,
  constraints: string,
  apiKey?: string,
  options?: { numSteps?: number; blendFrames?: number },
): Promise<MotionEditResponse> {
  const base = getExternalMogenBase();
  const url = `${base}/motion/edit`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey || DEFAULT_API_KEY,
    },
    body: JSON.stringify({ motionID, constraints, ...options }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to edit motion (${res.status}): ${body || res.statusText}`,
    );
  }
  return res.json();
}

/**
 * POST /motion/save — persist a BVH URL as a new motion.
 */
export async function saveMotion(
  bvhURL: string,
  name?: string,
  apiKey?: string,
): Promise<MotionSaveResponse> {
  const base = getExternalMogenBase();
  const url = `${base}/motion/save`;
  const payload: Record<string, string> = { bvhURL };
  if (name) payload.name = name;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey || DEFAULT_API_KEY,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to save motion (${res.status}): ${body || res.statusText}`,
    );
  }
  return res.json();
}
